import { motion } from 'framer-motion';
import type { HeatmapData } from '@contracts';

// Réplica 1:1 del heatmap "Horas Pico" del dashboard original (POS):
// grid CSS, color maroon rgba(112,19,19), horas recortadas a las activas,
// etiquetas de valor en celdas intensas y leyenda Bajo→Alto.
const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const fmt = (v: number) =>
  v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1000 ? `${Math.round(v / 1000)}k` : `${Math.round(v)}`;

export function HeatmapWidget({ data }: { data: HeatmapData }) {
  const flat = data.flat();
  const max = Math.max(...flat, 1);
  if (!flat.some((v) => v > 0)) {
    return <div className="grid h-full place-items-center text-sm text-slate-400">Sin datos</div>;
  }

  // Rango de horas con actividad (recorta las vacías, deja 1 de margen).
  let minHour = 23;
  let maxHour = 0;
  data.forEach((row) =>
    row.forEach((v, h) => {
      if (v > 0) {
        if (h < minHour) minHour = h;
        if (h > maxHour) maxHour = h;
      }
    }),
  );
  minHour = Math.max(0, minHour - 1);
  maxHour = Math.min(23, maxHour + 1);
  const hours = Array.from({ length: maxHour - minHour + 1 }, (_, i) => i + minHour);

  return (
    <div className="h-full overflow-auto">
      <div className="inline-block min-w-full">
        {/* Headers de horas */}
        <div className="mb-1 flex items-center gap-1">
          <div className="w-9 shrink-0" />
          {hours.map((h) => (
            <div key={h} className="min-w-[18px] flex-1 text-center text-[9px] font-bold tabular-nums text-slate-400">
              {h}
            </div>
          ))}
        </div>

        {/* Filas de días */}
        {DAYS.map((dayName, dayIdx) => (
          <div key={dayIdx} className="mb-1 flex items-center gap-1">
            <div className="w-9 shrink-0 text-[10px] font-bold uppercase text-slate-600">{dayName}</div>
            {hours.map((h) => {
              const v = data[dayIdx]?.[h] || 0;
              const intensity = v / max;
              const bg = v === 0 ? 'rgba(0,0,0,0.04)' : `rgba(112,19,19,${0.12 + intensity * 0.78})`;
              return (
                <motion.div
                  key={h}
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: dayIdx * 0.02 + h * 0.005 }}
                  whileHover={v > 0 ? { scale: 1.18, zIndex: 2 } : undefined}
                  className="relative aspect-square min-w-[18px] flex-1 rounded"
                  style={{ background: bg }}
                  title={v > 0 ? `${dayName} ${h}:00 — ${fmt(v)}` : ''}
                >
                  {intensity > 0.55 && (
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[8px] font-black text-white">
                      {fmt(v)}
                    </span>
                  )}
                </motion.div>
              );
            })}
          </div>
        ))}

        {/* Leyenda */}
        <div className="mt-3 flex items-center justify-between gap-2 text-[10px] text-slate-500">
          <span className="italic">Por día y hora</span>
          <div className="flex items-center gap-2">
            <span>Bajo</span>
            <div className="flex gap-0.5">
              {[0.15, 0.35, 0.55, 0.75, 0.95].map((i) => (
                <div key={i} className="h-3 w-3 rounded-sm" style={{ background: `rgba(112,19,19,${i})` }} />
              ))}
            </div>
            <span>Alto</span>
          </div>
        </div>
      </div>
    </div>
  );
}
